import { SyncCoreDataDefinitionsEnumTranslator, TaskErrorType, TaskStatus } from '@edgebox/sync-core-data-definitions';

/**
 * The slice of an operation error these helpers need. Declared structurally so callers can pass
 * the entity objects straight through; `type` must be present, because the whole point is telling
 * a deliberate wait apart from a failure.
 */
export interface IOperationErrorLike {
  timestamp: number;
  errorMessage?: string;
  type?: TaskErrorType;
}

/** The slice of an operation these helpers need. */
export interface IOperationLike {
  status?: string;
  errors?: IOperationErrorLike[];
}

/**
 * A deliberate wait for an external system. Not a failure: the work was handed over successfully
 * and the task is being re-delivered to check on it.
 */
export function isWaitingErrorType(type?: TaskErrorType): boolean {
  return type === TaskErrorType.AwaitingExternalJob;
}

/** Newest first. The backend appends one entry per delivery, so order is by timestamp. */
export function sortedOperationErrors(operation: IOperationLike): IOperationErrorLike[] {
  return [...(operation.errors || [])].sort((a, b) => b.timestamp - a.timestamp);
}

/** The entry that describes what the operation is doing right now. */
export function latestOperationError(operation: IOperationLike): IOperationErrorLike | undefined {
  return sortedOperationErrors(operation)[0];
}

/**
 * Whether the operation is currently waiting rather than failing. The status alone cannot answer
 * this: a waiting operation is still `Retrying`, so the newest error entry decides.
 */
export function isOperationWaiting(operation: IOperationLike): boolean {
  if (!operation.status?.includes(TaskStatus.Retrying)) {
    return false;
  }

  return isWaitingErrorType(latestOperationError(operation)?.type);
}

/** Whether any entry in the operation's history is a genuine failure rather than a wait. */
export function hasGenuineError(operation: IOperationLike): boolean {
  return (operation.errors || []).some((error) => !isWaitingErrorType(error.type));
}

/**
 * The entries to show while the error list is collapsed.
 *
 * A waiting operation appends a new entry on every poll, so the newest entry is very often a wait.
 * Showing only the newest would bury an earlier genuine failure behind the "+N" toggle, which is
 * exactly the case where an operator must not be told everything is fine — so the most recent real
 * error is always kept alongside it.
 */
export function collapsedOperationErrors(errors: IOperationErrorLike[]): IOperationErrorLike[] {
  const [newest] = errors;
  if (!newest) {
    return [];
  }

  if (!isWaitingErrorType(newest.type)) {
    return [newest];
  }

  const genuine = errors.find((error) => !isWaitingErrorType(error.type));

  return genuine ? [newest, genuine] : [newest];
}

/** How a single error entry should be presented, based on its own type. */
export function operationErrorTone(error: IOperationErrorLike): 'waiting' | 'error' {
  return isWaitingErrorType(error.type) ? 'waiting' : 'error';
}

/**
 * Operations that need attention.
 *
 * A deliberate wait is healthy and is excluded — otherwise a task that is simply waiting for a
 * provider lights up the amber "Warnings" tile as though something needed looking at. An operation
 * that is waiting *now* but failed for real earlier is still counted: the wait must not erase a
 * failure that already happened.
 */
export function countOperationsNeedingAttention(operations: IOperationLike[]): number {
  return operations.filter(
    (operation) => operation.status?.includes(TaskStatus.Retrying) && (!isOperationWaiting(operation) || hasGenuineError(operation))
  ).length;
}

/** Operations that are waiting on an external system. */
export function countWaitingOperations(operations: IOperationLike[]): number {
  return operations.filter((operation) => isOperationWaiting(operation)).length;
}

/**
 * The label for a waiting operation. Sourced from the shared enum translation so the wording stays
 * identical everywhere the type is shown.
 */
export function waitingStatusLabel(): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaskErrorType', TaskErrorType.AwaitingExternalJob);
}
